/**
 * Débordement d'un ajout qui comble un trou plus petit que lui.
 *
 * Le bouton « Ajouter » vise un trou libre entre deux éléments. Quand l'élément
 * posé est plus long que le trou, il faut rendre le surplus quelque part : c'est
 * toujours un infoservice qui cède, celui qui borde le trou en haut ou en bas,
 * au choix de l'opérateur. Ce module ne décide rien tout seul — il calcule le
 * surplus, dit si un côté peut l'absorber, et où l'élément se pose une fois la
 * place ouverte. La manœuvre elle-même reste celle de `openInsertionSpace`.
 */

import type { ScheduleItem } from '../../types/index.js'
import type { InfoserviceInsertPlan, InfoserviceResizeEdge } from './infoserviceResize.js'

/** Le trou libre visé par l'ajout, et les infoservices qui le bordent. */
export interface AddGap {
  /** Début du trou : la fin de l'élément qui le précède. */
  startMs: number
  /** Fin du trou : le début de l'élément qui le suit. */
  endMs: number
  /**
   * Manœuvres disponibles pour absorber un éventuel débordement. `END` cède du
   * haut (fin de la plage amont), `START` cède du bas (début de la plage aval).
   * Zéro à deux entrées, selon les infoservices réellement atteignables.
   */
  plans: InfoserviceInsertPlan[]
}

/** Fin la plus proche sous `boundaryMs` : le haut du trou qui précède. */
export function nearestEndBelow(items: ScheduleItem[], excludeId: string, boundaryMs: number): number | null {
  let best: number | null = null
  for (const item of items) {
    if (item.id === excludeId) continue
    const end = new Date(item.endTime).getTime()
    if (end <= boundaryMs && (best === null || end > best)) best = end
  }
  return best
}

/** Début le plus proche au-dessus de `boundaryMs` : le bas du trou qui suit. */
export function nearestStartAbove(items: ScheduleItem[], excludeId: string, boundaryMs: number): number | null {
  let best: number | null = null
  for (const item of items) {
    if (item.id === excludeId) continue
    const start = new Date(item.startTime).getTime()
    if (start >= boundaryMs && (best === null || start < best)) best = start
  }
  return best
}

/** Secondes dont l'élément déborde du trou (0 s'il y rentre). */
export function gapOverflowSec(gap: AddGap, durationSec: number): number {
  const gapSec = Math.round((gap.endMs - gap.startMs) / 1_000)
  return Math.max(0, durationSec - gapSec)
}

/** Un côté n'absorbe le débordement que si sa réserve cédable le couvre. */
export function gapSideFeasible(plan: InfoserviceInsertPlan | undefined | null, overflowSec: number): boolean {
  if (overflowSec <= 0) return true
  return !!plan && plan.slackMs >= overflowSec * 1_000
}

/**
 * Sens d'ancrage d'un ajout dans un trou, et l'heure qui l'ancre.
 *
 * Tant que l'élément tient dans le trou, c'est le bord cliqué qui fait foi :
 * « Ajouter » sous un bloc commence à sa fin, au-dessus d'un bloc finit à son
 * début. Le côté d'infoservice choisi ne compte qu'en cas de débordement. Le
 * laisser décider sans débordement retournait l'ancrage — un ajout sous un
 * programme suivi d'aucune plage finissait à la fin de ce programme, donc par
 * dessus lui.
 */
export function gapAddAnchor(
  gap: AddGap,
  durationSec: number,
  clickedAnchor: InfoserviceResizeEdge,
  chosenEdge: InfoserviceResizeEdge | null | undefined,
): { anchor: InfoserviceResizeEdge; edgeMs: number } {
  const anchor = gapOverflowSec(gap, durationSec) > 0 && chosenEdge ? chosenEdge : clickedAnchor
  return { anchor, edgeMs: anchor === 'END' ? gap.endMs : gap.startMs }
}

/**
 * Emplacement final de l'élément : il occupe tout le trou et déborde du côté
 * cédé. `END` (on rogne le haut) → l'élément finit au bas du trou ; `START` (on
 * rogne le bas) → il commence en haut du trou.
 */
export function gapOverflowPlacement(
  gap: AddGap,
  edge: InfoserviceResizeEdge,
  durationSec: number,
): { startMs: number; endMs: number } {
  const durationMs = durationSec * 1_000
  return edge === 'END'
    ? { startMs: gap.endMs - durationMs, endMs: gap.endMs }
    : { startMs: gap.startMs, endMs: gap.startMs + durationMs }
}
